/* RSC Health -- cart page behaviors (Phase 3; flow mockup #screen-cart +
   rsc-cart-coverage-mockup.html). Plain ES5 IIFE, no dependencies, guarded on
   [data-rsc-cart]. Drives the composition coverage engine + reversible ladder via
   Dawn's AJAX Cart API, acting only on the ids/keys in the server-rendered
   <script data-rsc-cart-map> JSON (never on names).

   COVERAGE (from the server map):
     absorbCoveredKeys : line keys of singles a cart panel already covers. On page
                         load we remove them so a covered test is never double-charged
                         (Behavior 2). The panel surfaces them in its "Includes" list.
     bumps[]           : { panelVariantId, absorbKeys[] } -- clicking a bump card
                         removes the grouped single lines and adds the panel
                         (Behavior 1). After reload those tests show as Included.

   LADDER (unchanged): upgrade removes the base line + adds the bigger panel carrying
   hidden props so switch-back survives a reload; switchback reverses it.

   After any mutation we window.location.reload() so the section re-renders from live
   cart state. Every fetch is guarded with .catch so a failed request restores the
   button instead of leaving it stuck. */
(function () {
  'use strict';

  var root = document.querySelector('[data-rsc-cart]');
  if (!root) return;

  /* ---- server map (ids/keys/prices-in-cents) ---- */
  var map = null;
  var mapEl = root.querySelector('[data-rsc-cart-map]');
  if (mapEl) {
    try { map = JSON.parse(mapEl.textContent); } catch (e) { map = null; }
  }

  /* ---- helpers ---- */
  function closest(el, selector) {
    while (el && el !== document) {
      if (el.nodeType === 1 && el.matches && el.matches(selector)) return el;
      el = el.parentNode;
    }
    return null;
  }

  function reload() { window.location.reload(); }

  function busy(btn) {
    if (!btn) return;
    btn.disabled = true;
    btn.setAttribute('data-rsc-label', btn.textContent);
    btn.textContent = 'Updating...';
  }
  function unbusy(btn) {
    if (!btn) return;
    btn.disabled = false;
    var label = btn.getAttribute('data-rsc-label');
    if (label != null) btn.textContent = label;
  }

  var JSON_HEADERS = { 'Content-Type': 'application/json', 'Accept': 'application/json' };

  /* change.js accepts the line-item KEY as the id field -- stable across cart reorders. */
  function changeLine(id, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({ id: id, quantity: quantity })
    }).then(function (r) { if (!r.ok) throw new Error('cart change failed'); return r.json(); });
  }

  function addVariant(variantId, quantity, properties) {
    var item = { id: variantId, quantity: quantity };
    if (properties) item.properties = properties;
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({ items: [item] })
    }).then(function (r) { if (!r.ok) throw new Error('cart add failed'); return r.json(); });
  }

  /* remove a list of line keys sequentially (avoids index-shift races) */
  function removeKeys(keys) {
    var chain = Promise.resolve();
    for (var i = 0; i < keys.length; i++) {
      (function (k) { if (!k) return; chain = chain.then(function () { return changeLine(k, 0); }); })(keys[i]);
    }
    return chain;
  }

  var NOTES_KEY = 'rscCartNotes';
  var INCL_KEY = 'rscInclNotes';

  /* ============ auto-resolve on load: redundant panels + covered singles ============
     Two things must never become charged lines:
       - a panel whose tests are fully inside a bigger cart panel (upgrade-only rule), and
       - a single a cart panel already covers (Behavior 2).
     We remove both in one pass, then reload; the reloaded map no longer lists them, so
     this runs at most once per change (no loop). Panel removals stash a note in
     sessionStorage so the reloaded page can explain the swap-up / rejected downgrade.
     Guarded so a failure leaves the cart untouched rather than reload-looping. */
  var absorbing = false;
  function autoResolve() {
    var pkeys = (map && map.absorbPanelKeys) || [];
    var ckeys = (map && map.absorbCoveredKeys) || [];
    var notes = (map && map.panelNotes) || [];
    var incl = (map && map.includedNotes) || [];
    var toRemove = [];
    var i;
    for (i = 0; i < pkeys.length; i++) { if (pkeys[i]) toRemove.push(pkeys[i]); }
    for (i = 0; i < ckeys.length; i++) { if (ckeys[i]) toRemove.push(ckeys[i]); }
    if (!toRemove.length) return false;
    absorbing = true;
    /* stash the receipts BEFORE removing the lines, so after the reload the page can tell the
       customer their covered test was kept in the panel and not charged twice (it no longer
       appears as its own line, so without this it would silently vanish). */
    if (notes.length) { try { sessionStorage.setItem(NOTES_KEY, JSON.stringify(notes)); } catch (e) {} }
    if (incl.length) { try { sessionStorage.setItem(INCL_KEY, JSON.stringify(incl)); } catch (e) {} }
    removeKeys(toRemove).then(reload).catch(function () { absorbing = false; });
    return true;
  }

  /* render + clear any panel-resolution notes stashed before the last reload */
  function renderNotes() {
    var box = root.querySelector('[data-rsc-notes]');
    if (!box) return;
    var notes, incl;
    try { notes = JSON.parse(sessionStorage.getItem(NOTES_KEY) || '[]'); } catch (e) { notes = []; }
    try { incl = JSON.parse(sessionStorage.getItem(INCL_KEY) || '[]'); } catch (e) { incl = []; }
    try { sessionStorage.removeItem(NOTES_KEY); } catch (e) {}
    try { sessionStorage.removeItem(INCL_KEY); } catch (e) {}
    if (!notes.length && !incl.length) return;
    var html = '';
    var i;
    for (i = 0; i < notes.length; i++) {
      var n = notes[i] || {};
      var removed = String(n.removed || 'That panel');
      var kept = String(n.kept || 'a larger panel you already have');
      html += '<div class="rsc-note"><b>' + esc(removed) + '</b> is already included in <b>' + esc(kept) +
              '</b> - we kept the larger panel so you are not charged twice.</div>';
    }
    /* covered-single receipts: the test stays in the panel, was not charged again */
    for (i = 0; i < incl.length; i++) {
      var c = incl[i] || {};
      var test = String(c.test || 'That test');
      var panel = String(c.panel || 'your panel');
      html += '<div class="rsc-note incl-note">&#10003; <b>' + esc(test) + '</b> is already included in <b>' +
              esc(panel) + '</b> - kept in your panel, not charged twice.</div>';
    }
    box.innerHTML = html;
    box.hidden = false;
  }
  function esc(s) {
    return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  /* ================= BEHAVIOR 1: bump a single up to its panel ================= */
  function findBump(variantId) {
    var bumps = (map && map.bumps) || [];
    for (var i = 0; i < bumps.length; i++) {
      if (String(bumps[i].panelVariantId) === String(variantId)) return bumps[i];
    }
    return null;
  }
  function doBump(btn, variantId) {
    var b = findBump(variantId);
    if (!b) { unbusy(btn); return; }
    /* remove the grouped single lines, then add the panel. On reload those tests read
       as Included (the panel covers them) -- no double charge. */
    removeKeys(b.absorbKeys || []).then(function () {
      return addVariant(parseInt(b.panelVariantId, 10), 1);
    }).then(reload).catch(function () { unbusy(btn); });
  }

  /* ================= LADDER: panel -> bigger panel (reversible) ================= */
  function doUpgrade(btn) {
    var u = map && map.upgrade;
    if (!u) { unbusy(btn); return; }
    var toRemove = [];
    if (u.baseKey) toRemove.push(u.baseKey);
    removeKeys(toRemove).then(function () {
      return addVariant(u.upgradeVariantId, 1, {
        '_rsc_upgraded_from': String(u.baseVariantId),
        '_rsc_base_price': String(u.basePrice),
        '_rsc_base_title': u.baseTitle || ''
      });
    }).then(reload).catch(function () { unbusy(btn); });
  }

  function doSwitchback(btn) {
    var s = map && map.switchback;
    if (!s) { unbusy(btn); return; }
    changeLine(s.upgradeKey, 0).then(function () {
      return addVariant(parseInt(s.baseVariantId, 10), 1);
    }).then(reload).catch(function () { unbusy(btn); });
  }

  /* ---- single delegated click handler ---- */
  root.addEventListener('click', function (e) {
    if (absorbing) { e.preventDefault(); return; }
    var t = e.target;

    var bumpBtn = closest(t, '[data-rsc-bump-add]');
    if (bumpBtn) {
      e.preventDefault();
      busy(bumpBtn);
      doBump(bumpBtn, bumpBtn.getAttribute('data-rsc-bump-add'));
      return;
    }

    /* same-visit add-on / wellness cross-sell: add the variant, then reload so the
       coverage engine re-evaluates (the added add-on now covers its code, so its own
       offer row disappears and any redundant upgrade is suppressed). */
    var addonBtn = closest(t, '[data-rsc-addon-add]');
    if (addonBtn) {
      e.preventDefault();
      busy(addonBtn);
      addVariant(parseInt(addonBtn.getAttribute('data-rsc-addon-add'), 10), 1)
        .then(reload).catch(function () { unbusy(addonBtn); });
      return;
    }

    var removeBtn = closest(t, '[data-rsc-remove]');
    if (removeBtn) {
      e.preventDefault();
      busy(removeBtn);
      changeLine(removeBtn.getAttribute('data-rsc-remove'), 0)
        .then(reload).catch(function () { unbusy(removeBtn); });
      return;
    }

    var incToggle = closest(t, '[data-rsc-includes-toggle]');
    if (incToggle) {
      e.preventDefault();
      var list = root.querySelector('[data-rsc-includes]');
      if (list) {
        var open = list.style.display === 'block';
        list.style.display = open ? 'none' : 'block';
        incToggle.setAttribute('aria-expanded', open ? 'false' : 'true');
      }
      return;
    }

    var upBtn = closest(t, '[data-rsc-upgrade-action]');
    if (upBtn) {
      e.preventDefault();
      busy(upBtn);
      var action = upBtn.getAttribute('data-rsc-upgrade-action');
      if (action === 'upgrade') { doUpgrade(upBtn); }
      else if (action === 'switchback') { doSwitchback(upBtn); }
      return;
    }
  });

  /* auto-resolve first; if nothing to remove, surface any note from the prior pass */
  if (!autoResolve()) { renderNotes(); }
})();
